import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { getModuleAssignments, removeAssignment } from '../../services/lessonService';

interface Assignment {
  assignment_id: string;
  student_id: string;
  student_name: string;
  student_email: string;
  status: string;
  due_date: string | null;
  assigned_at: string;
  completed_at: string | null;
  instructions: string | null;
}

interface ViewAssignmentsDialogProps {
  open: boolean;
  onClose: () => void;
  moduleId: string;
  moduleTitle: string;
}

const ViewAssignmentsDialog: React.FC<ViewAssignmentsDialogProps> = ({
  open,
  onClose,
  moduleId,
  moduleTitle,
}) => {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    open: boolean;
    assignmentId: string;
    studentName: string;
  }>({ open: false, assignmentId: '', studentName: '' });

  useEffect(() => {
    if (open && moduleId) {
      loadAssignments();
    }
  }, [open, moduleId]);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getModuleAssignments(moduleId);
      setAssignments(response.assignments || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'assigned':
        return 'info';
      case 'in_progress':
        return 'warning';
      case 'completed':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No due date';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleDeleteClick = (assignmentId: string, studentName: string) => {
    setConfirmDelete({ open: true, assignmentId, studentName });
  };

  const handleDeleteCancel = () => {
    setConfirmDelete({ open: false, assignmentId: '', studentName: '' });
  };

  const handleDeleteConfirm = async () => {
    const { assignmentId } = confirmDelete;
    setConfirmDelete({ open: false, assignmentId: '', studentName: '' });
    setDeletingId(assignmentId);

    try {
      await removeAssignment(assignmentId);
      // Refresh the assignments list
      await loadAssignments();
    } catch (err: any) {
      setError(err.message || 'Failed to remove assignment');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Assignments for: {moduleTitle}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : assignments.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No students have been assigned this lesson yet.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Use the "Assign to Students" button to assign this lesson.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Assigned</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.map((assignment) => (
                  <TableRow key={assignment.assignment_id}>
                    <TableCell>{assignment.student_name}</TableCell>
                    <TableCell>{assignment.student_email}</TableCell>
                    <TableCell>
                      <Chip
                        label={assignment.status.replace('_', ' ')}
                        color={getStatusColor(assignment.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(assignment.due_date)}</TableCell>
                    <TableCell>
                      {new Date(assignment.assigned_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Remove assignment">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            handleDeleteClick(assignment.assignment_id, assignment.student_name)
                          }
                          disabled={deletingId === assignment.assignment_id}
                        >
                          {deletingId === assignment.assignment_id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DeleteIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {assignments.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Total: {assignments.length} student{assignments.length !== 1 ? 's' : ''} assigned
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDelete.open}
        onClose={handleDeleteCancel}
        aria-labelledby="confirm-delete-title"
        aria-describedby="confirm-delete-description"
      >
        <DialogTitle id="confirm-delete-title">Confirm Remove Assignment</DialogTitle>
        <DialogContent>
          <Typography id="confirm-delete-description">
            Are you sure you want to remove the assignment for{' '}
            <strong>{confirmDelete.studentName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This action cannot be undone. The student will no longer have access to this lesson.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            Remove Assignment
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ViewAssignmentsDialog;
